import React, {useState} from 'react';
import {ChevronDown, ChevronUp} from 'lucide-react';
import {TestEducation} from '../../data/testEducation';
import ProgressBar from '../charts/ProgressBar';
import TestingHistory from '../charts/TestingHistory';
import {BIOMARKER_STATUS_COLORS, BIOMARKER_VALUE_COLOR} from '../../constants/colors';
import {getCustomBiomarkerConfig, isValueOnlyDisplay} from '../../utils/customBiomarkers';
import {isBinaryTest, getBinaryTestStatus} from '../../utils/biomarkerUtils';
import {BiomarkerStatus} from '../../types/biomarker';

/**
 * Helper function to format text with bold titles (lines ending with ':')
 */
const formatTextWithBoldTitles = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    return lines.map((line, index) => {
        // Check if line ends with ':' (title line)
        const colonMatch = line.match(/^(.+:)(.*)$/);
        if (colonMatch) {
            const [, title, rest] = colonMatch;
            return (
                <React.Fragment key={index}>
                    <strong>{title}</strong>
                    {rest}
                    {index < lines.length - 1 && '\n'}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && '\n'}
            </React.Fragment>
        );
    });
};

interface TestingHistoryData {
    date: string;
    value: number;
    label?: string;
}

interface BiomarkerChartCardProps {
    name: string;
    value: number;
    units: string;
    status?: BiomarkerStatus;
    rangeMin?: number;
    rangeMax?: number;
    history?: TestingHistoryData[];
    lastTestDate?: string;
    className?: string;
    education?: TestEducation | null;
    result?: any;
    recommendations?: any[];
    gender?: 'male' | 'female';
}

const BiomarkerChartCard: React.FC<BiomarkerChartCardProps> = ({
    name,
    value,
    units,
    status,
    rangeMin = 0,
    rangeMax = 100,
    history = [],
    lastTestDate,
    className = '',
    education,
    result,
    recommendations = [],
    gender,
}) => {
    const [expanded, setExpanded] = useState(false);

    // Check for custom biomarker configuration (with gender for gender-specific biomarkers)
    const customConfig = getCustomBiomarkerConfig(name, gender);
    const isValueOnly = isValueOnlyDisplay(name, 'singleResult');

    // Check if this is a binary test (Positive/Negative)
    const isBinary = result?.result ? isBinaryTest(result.result) : false;
    const binaryStatus = isBinary ? getBinaryTestStatus(result.result) : null;

    // Build progress bar sections based on custom config or default
    const progressBarSections = React.useMemo(() => {
        // Binary tests use 2-zone layout (green for negative, pink for positive)
        if (isBinary) {
            return [
                {width: 50, color: BIOMARKER_STATUS_COLORS.NORMAL}, // Green for negative
                {width: 50, color: BIOMARKER_STATUS_COLORS.HIGH}, // Pink for positive
            ];
        }

        if (!customConfig || customConfig.zones.length === 0) {
            // Default 3-zone layout
            return [
                {width: 33.33, color: BIOMARKER_STATUS_COLORS.LOW},
                {width: 33.33, color: BIOMARKER_STATUS_COLORS.NORMAL},
                {width: 33.33, color: BIOMARKER_STATUS_COLORS.HIGH},
            ];
        }

        // Custom zones from config
        return customConfig.zones.map((zone) => ({
            width: zone.percentage,
            color: zone.color,
        }));
    }, [customConfig, isBinary]);

    // Calculate optimal zone info (label, position, min/max values)
    const optimalZoneInfo = React.useMemo(() => {
        if (!customConfig || customConfig.zones.length === 0) {
            // Default: optimal zone is middle (33.33-66.67%)
            return {
                label: 'Optimal',
                startPercent: 33.33,
                endPercent: 66.67,
                minValue: rangeMin,
                maxValue: rangeMax,
            };
        }

        // Find the zone with "Normal" or "Optimal" status (the green zone)
        let cumulativePercent = 0;
        const optimalZone = customConfig.zones.find((zone) => zone.status === 'Normal' || zone.status === 'Optimal');

        if (!optimalZone) {
            // Fallback to default
            return {
                label: 'Optimal',
                startPercent: 33.33,
                endPercent: 66.67,
                minValue: rangeMin,
                maxValue: rangeMax,
            };
        }

        // Calculate position for this zone
        for (const zone of customConfig.zones) {
            if (zone === optimalZone) break;
            cumulativePercent += zone.percentage;
        }

        return {
            label: optimalZone.label, // Use the zone's label (e.g., "In Range" for Cortisol)
            startPercent: cumulativePercent,
            endPercent: cumulativePercent + optimalZone.percentage,
            minValue: optimalZone.min,
            maxValue: optimalZone.max,
        };
    }, [customConfig, rangeMin, rangeMax]);

    // Find recommendations that match this biomarker
    const getMatchingRecommendations = () => {
        if (!recommendations || !result) return [];

        return recommendations.filter((rec: any) => {
            if (!rec.biomarkers || rec.biomarkers.length === 0) return false;

            return rec.biomarkers.some((biomarker: any) => {
                // Match by test name (case insensitive)
                const testName = result.test_name?.toLowerCase() || '';
                const biomarkerType = biomarker.test_type?.toLowerCase() || '';
                return testName.includes(biomarkerType) || biomarkerType.includes(testName);
            });
        });
    };

    const matchingRecommendations = getMatchingRecommendations();

    // Calculate position on range bar (0-100%)
    // The progress bar is divided into 3 zones:
    // - 0-33.33%: Low zone (orange)
    // - 33.33-66.67%: Normal/Optimal zone (green) - this is where the reference range maps to
    // - 66.67-100%: High zone (pink)
    const getPositionPercentage = () => {
        // Binary tests: position at 25% for Negative (green zone), 75% for Positive (pink zone)
        if (isBinary && binaryStatus) {
            return binaryStatus === 'Negative' ? 25 : 75;
        }

        // If custom config exists, use custom zone logic
        if (customConfig && customConfig.zones.length > 0) {
            let cumulativePercentage = 0;

            // Find which zone the value falls into and calculate position within that zone
            for (let i = 0; i < customConfig.zones.length; i++) {
                const zone = customConfig.zones[i];
                const zoneStart = cumulativePercentage;

                // Use >= for min and < for max, except for the last zone which uses <=
                const inZone =
                    i === customConfig.zones.length - 1
                        ? value >= zone.min && value <= zone.max
                        : value >= zone.min && value < zone.max;

                if (inZone) {
                    // Value is within this zone
                    const positionInZone = (value - zone.min) / (zone.max - zone.min);
                    return zoneStart + positionInZone * zone.percentage;
                }

                cumulativePercentage += zone.percentage;
            }

            // If value is outside all zones, clamp to edges
            const firstZone = customConfig.zones[0];
            const lastZone = customConfig.zones[customConfig.zones.length - 1];

            if (value < firstZone.min) {
                // Map to first portion of first zone (proportionally)
                const range = firstZone.max - firstZone.min;
                const distance = firstZone.min - value;
                const proportionalPosition = Math.max(0, (range - distance) / range);
                return proportionalPosition * firstZone.percentage;
            }

            if (value > lastZone.max) {
                // Map to last portion of last zone (proportionally)
                const totalBeforeLast = customConfig.zones.slice(0, -1).reduce((sum, z) => sum + z.percentage, 0);
                const range = lastZone.max - lastZone.min;
                const distance = value - lastZone.max;
                const proportionalPosition = Math.min(1, (range + distance) / (range * 2));
                return totalBeforeLast + proportionalPosition * lastZone.percentage;
            }
        }

        // Fall back to standard 3-zone logic
        const rangeWidth = rangeMax - rangeMin;

        // Define the three zones
        const LOW_ZONE_END = 33.33;
        const NORMAL_ZONE_START = 33.33;
        const NORMAL_ZONE_END = 66.67;
        const HIGH_ZONE_START = 66.67;

        // If the value is within the reference range, map it to the normal zone (33.33-66.67%)
        if (value >= rangeMin && value <= rangeMax) {
            const positionInRange = (value - rangeMin) / rangeWidth;
            const normalZoneWidth = NORMAL_ZONE_END - NORMAL_ZONE_START;
            return NORMAL_ZONE_START + positionInRange * normalZoneWidth;
        }

        // If the value is below the reference range, map it to the low zone (0-33.33%)
        if (value < rangeMin) {
            const lowZoneMin = rangeMin - rangeWidth;
            const lowZoneMax = rangeMin;
            const clampedValue = Math.max(value, lowZoneMin);
            const positionInLowZone = (clampedValue - lowZoneMin) / (lowZoneMax - lowZoneMin);
            return positionInLowZone * LOW_ZONE_END;
        }

        // If the value is above the reference range, map it to the high zone (66.67-100%)
        if (value > rangeMax) {
            const highZoneMin = rangeMax;
            const highZoneMax = rangeMax + rangeWidth;
            const clampedValue = Math.min(value, highZoneMax);
            const positionInHighZone = (clampedValue - highZoneMin) / (highZoneMax - highZoneMin);
            const highZoneWidth = 100 - HIGH_ZONE_START;
            return HIGH_ZONE_START + positionInHighZone * highZoneWidth;
        }

        // Fallback
        return 50;
    };

    // Helper to get status color class
    const getStatusColorClass = (currentStatus: string | undefined) => {
        if (currentStatus === 'Normal' || currentStatus === 'Negative') return 'text-mld-status-normal';
        if (currentStatus === 'High' || currentStatus === 'Positive') return 'text-mld-status-high';
        if (currentStatus === 'Low') return 'text-mld-status-low';
        return 'text-gray-500';
    };

    return (
        <div
            className={`bg-white rounded-lg border border-gray-200 ${className} ${education ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
            <div className="p-3 lg:p-5" onClick={() => education && setExpanded(!expanded)}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-2 sm:gap-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <h3 className="text-sm lg:text-base font-medium text-gray-900">{name}</h3>
                        {!isValueOnly && status && (
                            <div className="flex items-center gap-2">
                                <span
                                    className={`text-xs font-medium ${getStatusColorClass(isBinary && binaryStatus ? binaryStatus : status)}`}>
                                    {isBinary && binaryStatus ? binaryStatus : status}
                                </span>
                                {status === 'Normal' || status === 'Optimal' || status === 'Negative' ? (
                                    <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-normal flex items-center justify-center">
                                        <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                            <path
                                                d="M1 3L2.5 4.5L7 0.5"
                                                stroke={BIOMARKER_STATUS_COLORS.NORMAL}
                                                strokeWidth="1.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                ) : status === 'High' || status === 'Positive' ? (
                                    <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-high flex items-center justify-center">
                                        <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                            <path
                                                d="M1 0V6.5M1 8.5V9.5"
                                                stroke={BIOMARKER_STATUS_COLORS.HIGH}
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                ) : status === 'Low' || status === 'Elevated' ? (
                                    <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-low flex items-center justify-center">
                                        <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                            <path
                                                d="M1 0V6.5M1 8.5V9.5"
                                                stroke={BIOMARKER_STATUS_COLORS.LOW}
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                ) : status === 'Borderline' ? (
                                    <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-borderline flex items-center justify-center">
                                        <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                            <path
                                                d="M1 0V6.5M1 8.5V9.5"
                                                stroke={BIOMARKER_STATUS_COLORS.BORDERLINE}
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {matchingRecommendations.map((rec: any, index: number) => (
                            <span
                                key={rec.Id || index}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium self-start bg-amber-50 text-amber-700 border border-amber-200"
                                title={rec.content}>
                                {rec.name}
                            </span>
                        ))}
                    </div>
                    {education && (
                        <div className="flex items-center">
                            {expanded ? (
                                <ChevronUp className="h-5 w-5 text-gray-500" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-6">
                    {/* Left side - Current value and range */}
                    <div>
                        <div className="mb-4">
                            <span className="text-2xl lg:text-3xl font-bold" style={{color: BIOMARKER_VALUE_COLOR}}>
                                {isBinary && result?.result
                                    ? result.result
                                    : (() => {
                                          const rounded = Number(Number(value).toFixed(2));
                                          return rounded === Math.floor(rounded)
                                              ? rounded.toString()
                                              : rounded.toString();
                                      })()}
                            </span>
                            {!isBinary && <span className="text-sm text-gray-500 ml-2">{units}</span>}
                        </div>

                        {/* Range visualization using reusable ProgressBar */}
                        {!isValueOnly && (
                            <div className="mb-4">
                                <ProgressBar
                                    sections={progressBarSections}
                                    indicatorPosition={getPositionPercentage()}
                                    className="mb-0"
                                />

                                {/* Range labels - different layout for binary vs numeric tests */}
                                {isBinary ? (
                                    /* Binary tests: show Negative/Positive labels centered in each zone */
                                    <div className="relative mt-6 text-xs text-gray-500">
                                        <div className="flex justify-between px-4">
                                            <span className="text-center" style={{width: '50%'}}>
                                                Negative
                                            </span>
                                            <span className="text-center" style={{width: '50%'}}>
                                                Positive
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    /* Numeric tests: show min/max values with dashed lines */
                                    <div className="relative mt-6 text-xs text-gray-500">
                                        {/* Label positioned at center of optimal zone */}
                                        <div className="relative mb-3 flex">
                                            <span
                                                className="absolute"
                                                style={{
                                                    left: `${optimalZoneInfo.startPercent + (optimalZoneInfo.endPercent - optimalZoneInfo.startPercent) / 2}%`,
                                                    transform: 'translateX(-50%)',
                                                }}>
                                                {optimalZoneInfo.label}
                                            </span>
                                        </div>
                                        <div className="absolute top-0 left-0 w-full flex">
                                            {/* Min value with dashed line */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    left: `calc(${optimalZoneInfo.startPercent}% + 8px)`,
                                                    transform: 'translateX(-50%)',
                                                }}>
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        style={{
                                                            width: '2px',
                                                            height: '16px',
                                                            borderLeft: '2px dashed #9CA3AF',
                                                        }}
                                                    />
                                                    <span className="mt-2">{optimalZoneInfo.minValue}</span>
                                                </div>
                                            </div>
                                            {/* Max value with dashed line */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    left: `calc(${optimalZoneInfo.endPercent}% - 8px)`,
                                                    transform: 'translateX(-50%)',
                                                }}>
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        style={{
                                                            width: '2px',
                                                            height: '16px',
                                                            borderLeft: '2px dashed #9CA3AF',
                                                        }}
                                                    />
                                                    <span className="mt-2">{optimalZoneInfo.maxValue}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right side - Testing History (hidden for binary tests) */}
                    <div className="mt-4 lg:mt-0">
                        {isBinary ? (
                            /* Binary tests don't show testing history - just show the last tested date */
                            <div className="h-24 lg:h-32 flex items-center justify-center">
                                {lastTestDate && (
                                    <div className="text-sm text-gray-500">
                                        Last tested{' '}
                                        {new Date(lastTestDate).toLocaleDateString('en-US', {
                                            month: 'numeric',
                                            day: 'numeric',
                                            year: '2-digit',
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : history && history.length > 0 ? (
                            (() => {
                                // Transform history data and add current value for TestingHistory component
                                const chartData = [
                                    ...history.slice(-3).map((point) => ({
                                        name:
                                            point.label ||
                                            new Date(point.date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                year: '2-digit',
                                            }),
                                        value: point.value,
                                    })),
                                    {
                                        name: 'Current',
                                        value: value,
                                    },
                                ];

                                return (
                                    <TestingHistory
                                        data={chartData}
                                        title="Testing History"
                                        height={120}
                                        showGrid={true}
                                        showVerticalLines={true}
                                        thresholds={{low: rangeMin || 0, high: rangeMax || 100}}
                                        className="text-xs"
                                        labelFontSize="16px"
                                    />
                                );
                            })()
                        ) : (
                            <>
                                <div className="mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">Testing History</h4>
                                </div>
                                <div className="h-24 lg:h-32 flex items-center justify-center text-sm text-gray-500">
                                    No historical data available
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {lastTestDate && !isBinary && (
                    <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400 text-right">
                        Last tested{' '}
                        {new Date(lastTestDate).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: '2-digit',
                        })}
                    </div>
                )}
            </div>

            {/* Educational Information - Expanded Section */}
            {expanded && education && (
                <div className="border-t border-gray-200 bg-gray-50 px-3 lg:px-5 pb-3 lg:pb-5">
                    <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm mt-3 lg:mt-5">
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-blue-600 text-xs font-bold">i</span>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-base font-medium text-gray-900 mb-3">{education.title}</h3>

                                <div className="text-sm text-gray-700 space-y-3">
                                    <p>{education.description}</p>

                                    {/* Always show relevant status-specific information */}
                                    {education.lowLevel && (
                                        <>
                                            <p>
                                                <strong>{education.lowLevel.title}</strong> -{' '}
                                                {education.lowLevel.description}
                                            </p>
                                            {education.lowLevel.symptoms && education.lowLevel.symptoms.length > 0 && (
                                                <>
                                                    <p>
                                                        <strong>
                                                            Symptoms of low {name.toLowerCase()} can include:
                                                        </strong>
                                                    </p>
                                                    <ul className="list-none space-y-1 ml-4">
                                                        {education.lowLevel.symptoms.map((symptom, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 mr-2 flex-shrink-0"></span>
                                                                {symptom}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}
                                        </>
                                    )}

                                    {education.highLevel && (
                                        <>
                                            <p>
                                                <strong>{education.highLevel.title}</strong> -{' '}
                                                {education.highLevel.description}
                                            </p>
                                            {education.highLevel.symptoms &&
                                                education.highLevel.symptoms.length > 0 && (
                                                    <>
                                                        <p>
                                                            <strong>
                                                                Symptoms of elevated {name.toLowerCase()} can include:
                                                            </strong>
                                                        </p>
                                                        <ul className="list-none space-y-1 ml-4">
                                                            {education.highLevel.symptoms.map((symptom, index) => (
                                                                <li key={index} className="flex items-start">
                                                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 mr-2 flex-shrink-0"></span>
                                                                    {symptom}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </>
                                                )}
                                        </>
                                    )}

                                    {education.normalLevel && (
                                        <p>
                                            <strong>{education.normalLevel.title}</strong> -{' '}
                                            {education.normalLevel.description}
                                        </p>
                                    )}

                                    {/* Additional information */}
                                    {education.additionalInfo && (
                                        <p className="whitespace-pre-line">
                                            {formatTextWithBoldTitles(education.additionalInfo)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Lab notes if available */}
                        {result?.notes && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="flex items-start space-x-2">
                                    <div className="flex-shrink-0 mt-1">
                                        <span className="text-blue-500">📝</span>
                                    </div>
                                    <div className="text-sm text-gray-700">{result.notes}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(BiomarkerChartCard);
